/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import inventoryItemService from 'service/inventoryItem';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import { pageToOffset } from 'utils/pageToOffset';
import { useAppContext } from 'providers/AppProvider';
import { useWindowWidth } from 'utils/useWindowSize';
import Button from 'components/base/Button';
import inventoryService from 'service/inventory';

export type CreateInventoryData = {
  product_name: string;
  barcode: string;
  units: string;
  currency: string;
  cost: string;
  accounts_number: string;
  quantity_fact: string;
  shortage: string;
  surplus: string;
  deficiency_amount: string;
  surplus_amount: string;
  status: string;
  guid: string;
};

const InfoResultTable = () => {
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const { name, inventory_pid } = state;
  const {
    config: { theme }
  } = useAppContext();
  const windowSize: any = useWindowWidth();
  const url = process.env.REACT_APP_CDN_URL;

  const [searchValue, setSearchValue] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingExcel, setLoadingExcel] = useState(false);

  const { data, refetch, isFetching, isLoading } = useQuery(
    ['GET_INVENTORY_ITEM', searchValue, currentPage],
    () => {
      if (inventory_pid) {
        const res = inventoryItemService
          .getInventoryItemById(inventory_pid, {
            offset: pageToOffset(currentPage, 10),
            limit: 10,
            search: searchValue
          })
          .then((res: any) => res);

        return res;
      }
    },
    {
      enabled:
        !!inventory_pid ||
        !searchValue ||
        searchValue?.length > 2 ||
        !!currentPage
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const inventoryItemData: CreateInventoryData[] = useMemo(() => {
    const res =
      data?.inventory_items?.map((items: any) => {
        const packageQuantity = items?.nomenclature?.package_quantity || 1;

        const totalQuantity = items?.quantity
          ? Number(items?.quantity.toFixed(3))
          : 0;

        const fullPackagesTotal = Math.floor(totalQuantity / packageQuantity);
        const remainingQuantityTotal = Math.floor(
          totalQuantity % packageQuantity
        );
        const quantityDisplayTotal = `${fullPackagesTotal}/${remainingQuantityTotal}`;

        const quantity = items?.quantity_actual
          ? Number(items?.quantity_actual.toFixed(3))
          : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = Math.floor(quantity % packageQuantity);
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return {
          product_name: items?.product?.name,
          barcode: items?.product?.barcode,
          units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          cost: items?.price_in,
          accounts_number:
            items?.nomenclature?.measure_unit_kind_id ===
            '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
              ? quantityDisplayTotal
              : items?.quantity ?? 0,
          quantity_fact:
            items?.nomenclature?.measure_unit_kind_id ===
            '3c7cee55-ad41-4ee3-a409-a7bb5f428b36'
              ? quantityDisplay
              : items?.quantity_actual ?? 0,
          shortage: items?.shortage ?? 0,
          surplus: items?.excess ?? 0,
          deficiency_amount: items?.shortage ?? 0,
          surplus_amount: items?.excess_total ?? 0,
          status: items?.status?.name?.[i18n?.language],
          guid: items?.id,
          product_id: items?.product_id
        };
      }) ?? [];

    return res;
  }, [data]);

  const suppliersBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'inventory',
      url: '/inventory/create-inventory'
    },
    {
      label: 'inventory_information',
      url: '/inventory/info'
    },
    {
      label: name ?? '',
      active: true
    }
  ];

  const createInventoryDataTableColumns: ColumnDef<CreateInventoryData>[] = [
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'cost',
      header: t('price'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'accounts_number',
      header: t('quantity_accounting'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_fact',
      header: t('number_facts'),

      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'shortage',
      header: t('shortage'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'surplus',
      header: t('excess'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'deficiency_amount',
      header: t('amount_deficiency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'surplus_amount',
      header: t('amount_surplus'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: inventoryItemData,
    columns: createInventoryDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleExcelById = () => {
    setLoadingExcel(true);
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Fetch report and trigger download
    inventoryService
      ?.getInventoryListExcelById(inventory_pid)
      .then((res: any) => {
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        setLoadingExcel(false);
      });
  };

  useEffect(() => {
    if (searchValue?.length > 2 || searchValue === '') {
      refetch();
    }
  }, [searchValue]);

  return (
    <div>
      <PageBreadcrumb items={suppliersBreadcrumbItems} />

      <div>
        <h2 className="mb-5">{name ?? ''}</h2>

        <div className="d-flex align-items-center  gap-2 mb-5">
          <SearchBox
            placeholder={t('search')}
            onChange={e => setSearchValue(e?.target?.value)}
          />
          <Button
            className="d-flex align-items-center gap-2"
            style={{ height: '48px' }}
            variant="outline-primary"
            onClick={handleExcelById}
            loading={loadingExcel}
            loadingPosition="start"
          >
            <FeatherIcon icon="upload-cloud" size={20} />
            {windowSize > 768 && t('export')}
          </Button>
        </div>

        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
            <AdvanceTable
              tableProps={{ className: 'phoenix-table fs-9' }}
              isLoading={loading}
            />
            <AdvanceTableCPagination
              count={pageCount}
              page={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </AdvanceTableProvider>

        <div
          className={
            theme === 'dark' ? 'dark-footer-inv-create' : 'footer-inv-create'
          }
        >
          <div className="box d-flex flex-column gap-2">
            {data?.amount_items?.map((item: any) => (
              <div key={item} className="d-flex gap-2">
                <p className="mb-0 text-bold">
                  {t('total_amount_shortages')} (
                  {item?.currency?.name?.[i18n?.language]}):{' '}
                </p>
                <span>{item?.shortage_amount ?? 0}</span>
              </div>
            ))}
          </div>

          <div className="box d-flex flex-column gap-2">
            {data?.amount_items?.map((item: any) => (
              <div key={item} className="d-flex gap-2">
                <p className="mb-0 text-bold">
                  {t('total_amount_surplus')} (
                  {item?.currency?.name?.[i18n?.language]}):{' '}
                </p>
                <span>{item?.excess_amount ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoResultTable;
